// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesigner.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import uiState from 'logic/Izenda.UiState';
import m from 'mobx';
import Sidebar from 'share/components/layout/Izenda.Sidebar';
import MainContent from 'share/components/layout/Izenda.MainContent';
import MainWrapper from 'share/components/layout/Izenda.MainWrapper';
import Menu from './Izenda.ReportDesignerMenu';
import RenderSwitcher from 'share/components/controls/Izenda.RenderSwitcher';
import ReportDesignerContent from './Izenda.ReportDesignerContent';
import DataSource from './DataSource/Izenda.DataSource';
import Fields from './Fields/Izenda.Fields';
import Formats from './Formats/Izenda.Formats';
import Schedule from './Schedule/Izenda.Schedule';
import Access from './Access/Izenda.Access';
import Exporting from './Exporting/Izenda.Exporting';
import SettingsLevel from 'modules/Settings/SettingsLevel/Izenda.SettingsLevel';
import SettingsStore from 'logic/Izenda.SettingsStore';
import AppStore from 'logic/Izenda.AppStore';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import actions from './Izenda.ReportDesignerActions';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import Alert from 'share/components/controls/Izenda.Alert';
import SecurityManagerStore from 'logic/Izenda.SecurityManagerStore';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import { isNil, isEmpty, filter } from 'lodash/fp';

@connect(actions)
export default class ReportDesigner extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired
  };

  constructor(props: {}) {
    super(props);
    this.settingStore = new SettingsStore(true);
    this.securityManagerStore = new SecurityManagerStore();
  }

  componentWillMount() {
    this.init();
  }

  init() {
    const {
      props,
      securityManagerStore,
      props: { store, menuStore, loader, authorization, detectReportChange }
    } = this;

    loader.startLoading();
    store.init();

    // Get menu base on privileges & license
    menuStore.filterContextMenuByLicense(authorization);
    uiState.isMobile && menuStore.filterContextMenuForMobile();

    if (isNil(store.model.reportId)) {
      store.loadDefaultAccess().finally(loader.endLoading.bind(loader));
    } else {
      securityManagerStore
        .checkAccessPage(
          props,
          store.model.reportId,
          accessPriority => accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs
        )
        .bind(this)
        .then(canAccessPage => {
          canAccessPage &&
            store.cancelChanges().then(() => {
              store
                .loadReportDesigner()
                .bind(this)
                .then(this.getContextMenu)
                .then(reportDef => {
                  const noPermission =
                    reportDef &&
                    !isNil(reportDef.messages) &&
                    !isEmpty(reportDef.messages[0].detail) &&
                    reportDef.messages[0].detail === 'NoPermission';

                  return !noPermission ? detectReportChange() : null;
                })
                .finally(loader.endLoading.bind(loader));
            });

          !canAccessPage && loader.endLoading();
        });
    }

    // reload performance in advanced settings
    AppStore.loadPerformance();
    if (store.isNewReport) {
      menuStore.enableMenuItems.length > 0 && (menuStore.contextMenuActiveLink = menuStore.enableMenuItems[0].key);
    }

    this.setDisableSettingLevel(menuStore.contextMenuActiveLink);
  }

  /**
   * Only enable setting level in ReportDesigner when DataSource tab is opened
   * @param {string} contextMenuActiveLink
   */
  setDisableSettingLevel(contextMenuActiveLink: string) {
    contextMenuActiveLink === constants.PAGE_NAME.DATA_SOURCE
      ? uiState.enableSystemSetting()
      : uiState.updateDisableSystemSetting();
  }

  componentWillUpdate() {
    const { menuStore } = this.props;
    this.setDisableSettingLevel(menuStore.contextMenuActiveLink);
  }

  componentDidMount() {
    const props = this.props;
    // observe changing tenantId, then reload data for Report Designer
    this.disposeObserveTenantId = m.observe(uiState, 'tenantId', change => {
      if (uiState.silentUpdate) return;
      props.handleTenantChange(change);
    });
    // Observe the changes of cascading
    this.cascadingChangedDisposer = m.observe(
      this.props.store,
      'showCascadingChangedMessage',
      this.props.handleCascadingChanged
    );
    // Observe the changes of need update result
    this.needUpdateResultDisposer = m.observe(
      this.props.store,
      'needUpdateResult',
      this.props.handleNeedUpdateResultChanged
    );
  }

  componentWillUnmount() {
    this.disposeObserveTenantId && this.disposeObserveTenantId();
    this.cascadingChangedDisposer && this.cascadingChangedDisposer();
    this.needUpdateResultDisposer && this.needUpdateResultDisposer();
    this.props.store.destroy();
    this.settingStore.destroy();
  }

  /**
   * Register the update result function
   */
  registerUpdateResultFuntion = (updateResults: any): void => {
    this.updateResultFunction = updateResults;
  };
  /**
   * Handle when change page
   */
  onPageChange = (nextPage: string, currentPage: string): void => {
    if (nextPage === currentPage) {
      return;
    }

    const { store, onPageChanging } = this.props;
    const changePageStatus = {
      isChangePage: true,
      currentPage,
      nextPage
    };

    if (nextPage === constants.PAGE_NAME.FIELDS || nextPage === constants.PAGE_NAME.FORMATS) {
      store.switchToDesignReportContainer = false;
      this.updateResultFunction(changePageStatus).then(success => {
        success && onPageChanging(nextPage, currentPage, false);
      });
    } else {
      store.switchToDesignReportContainer = true;
      this.updateResultFunction(changePageStatus).then(success => {
        success && onPageChanging(nextPage, currentPage, true);
      });
    }
  };

  /**
   * Get context menu
   */
  getContextMenu = (reportDef: any): any => {
    const {
      props: {
        menuStore,
        authorization,
        isNewDefault,
        authorizationConstants: { PERMISSION_KEY },
        store: { dataSourceStore, advancedMode },
        menuStore: { contextMenuActiveLink }
      }
    } = this;
    if (advancedMode) {
      const hasDataSourcePermission =
        authorization.checkPrivileges([PERMISSION_KEY.ReportsDataSourcesAdvancedDataSources]) ||
        authorization.checkPrivileges([PERMISSION_KEY.FullReportAndDashboardAccess]);

      !hasDataSourcePermission && menuStore.excludeMenuItemByKey('DataSource');

      if (!hasDataSourcePermission || !isNewDefault) {
        const dataSourceStoreLoadInitialFunc = contextMenuActiveLink
          ? dataSourceStore.loadInitial
          : dataSourceStore.loadInitialWithUpdateFilter;

        // Load Data Source Data if not have this tab
        return dataSourceStoreLoadInitialFunc
          .call(dataSourceStore)
          .then(dataSourceStore.loadDataSourceDetail.bind(dataSourceStore))
          .then(() => {
            // all data is loaded sucessfully >> enable context menu
            menuStore.updateMenuState(true);
            menuStore.updateMenuStatus(true);
            return reportDef;
          });
      }
    }

    // init menu context, status = disabled
    // its status will be enabled when report data is loaded successfully
    menuStore.updateMenuStatus(true);
    return Promise.resolve(reportDef);
  };

  render() {
    const {
      settingStore,
      onPageChange,
      registerUpdateResultFuntion,
      props: {
        store,
        route,
        params,
        localization,
        disableReport,
        authorization,
        detectReportChange,
        menuStore,
        isNewDefault,
        location,
        saveDraftReport,
        store: { fieldsStore }
      }
    } = this;

    // Filter Report Designer tab by license
    const REPORT_DESIGNER_TAB = filter(
      tab => authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Scheduling) || tab !== 'Schedule',
      constants.REPORT_DESIGNER_TAB
    );

    return (
      <section className='izenda-Wrapper izenda-report-designer'>
        <SettingsLevel store={settingStore} />

        <MainWrapper settingStore={this.settingStore}>
          <div className='izenda-TableLayout u_paddingTopBottom'>
            {!isNil(menuStore.contextMenuActiveLink) && (
              <Sidebar isExpanded={isExpanded(menuStore)}>
                <Menu
                  menuStore={menuStore}
                  onPageChanging={onPageChange}
                  disableReportDesigner={store.disableReportDesigner}
                  dataSource={menuStore.visibleMenuItems}
                />
              </Sidebar>
            )}
            {!store.disableReportDesigner && !isNil(menuStore.contextMenuActiveLink) ? (
              <MainContent>
                <RenderSwitcher componentKey={menuStore.contextMenuActiveLink}>
                  {REPORT_DESIGNER_TAB.map(pageName => (
                    <RenderSwitcher.Item
                      key={pageName}
                      componentKey={pageName}
                      component={ReportDesignerContent}
                      pageName={pageName}
                      pageInfo={getPageInfo(pageName)}
                      store={store}
                      menuStore={menuStore}
                      fieldsStore={fieldsStore}
                      isNewDefault={isNewDefault}
                      disableReport={disableReport}
                      detectReportChange={detectReportChange}
                      route={route}
                      location={location}
                      saveDraftReport={saveDraftReport}
                      registerUpdateResultFuntion={registerUpdateResultFuntion}
                      params={params}
                    />
                  ))}
                </RenderSwitcher>
              </MainContent>
            ) : (
              !isNil(menuStore.contextMenuActiveLink) && (
                <section className='izenda-MainContent'>
                  <Alert type='error' autoHide={false}>
                    {localization.getText('COMMON_MESSAGES_NOPERMISSION_VIEWREPORT')}
                  </Alert>
                </section>
              )
            )}
          </div>
        </MainWrapper>
      </section>
    );
  }
}

function getPageInfo(pageName: string): any {
  switch (pageName) {
    case 'DataSource':
      return DataSource;
    case 'Fields':
      return Fields;
    case 'Formats':
      return Formats;
    case 'Exporting':
      return Exporting;
    case 'Schedule':
      return Schedule;
    case 'Access':
      return Access;
    default:
      throw 'Not found ' + pageName;
  }
}

/*  collapse Sidebar when switching to Field tab */
function isExpanded(menuStore: any): boolean {
  if (BrowserUtils.isMobileDevice()) {
    return true;
  }
  return menuStore.contextMenuActiveLink === constants.PAGE_NAME.DATA_SOURCE;
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Izenda.ReportDesigner.js